import dayjs from 'dayjs';

/** 阶段名（运行中文案，与预标注服务的 stage 取值一一对应，不裸出英文） */
const STAGE_TEXT = { downloading: '下载中', inferring: '推理中', injecting: '注入中' };
/** 阶段短名（失败文案的阶段前缀）：与服务侧错误文案的 [阶段] 前缀同一套词 */
const STAGE_NAME = { downloading: '下载', inferring: '推理', injecting: '注入' };
const PHASE_LABEL = {
  none: '未登记',
  pending: '排队中',
  dispatching: '派发中',
  dispatched: '已派发',
  downloading: '下载中',
  inferring: '推理中',
  injecting: '注入中',
  completed: '已完成',
  failed: '失败',
};
const PHASE_TONE = {
  none: 'info',
  pending: 'info',
  dispatching: 'info',
  dispatched: 'info',
  downloading: 'info',
  inferring: 'info',
  injecting: 'info',
  completed: 'success',
  failed: 'danger',
};

/** 预标注任务时间点（MM-DD HH:mm）；缺省/非法给空串，调用处据此降级（dayjs(undefined) 会取当前时间） */
export function preAnnotateAt(val) {
  if (!val) return '';
  const d = dayjs(val);
  return d.isValid() ? d.format('MM-DD HH:mm') : '';
}

/** 去掉旧服务错误文案里的 [阶段] 前缀，返回 { stage, text }。 */
function splitErrorStage(message) {
  const raw = String(message || '');
  const m = /^\[([^\]]{1,12})\]\s*/.exec(raw);
  return m ? { stage: m[1], text: raw.slice(m[0].length) } : { stage: '', text: raw };
}

function stageText(job) {
  const label = STAGE_TEXT[(job || {}).stage];
  if (!label) return '';
  const total = Number((job || {}).stage_total) || 0;
  const done = Number((job || {}).stage_done) || 0;
  return total > 0 ? `${label} ${done}/${total}` : label;
}

/**
 * 预标注全局状态唯一口径：phase/label/tone/text/summary/stageText。
 * 列表、弹窗、登记向导都从这里取文案；失败 summary 与日志详情分开，避免把 tail 塞进标题。
 */
export function preAnnotateState(job) {
  if (!job) {
    return { phase: 'none', label: PHASE_LABEL.none, tone: PHASE_TONE.none, text: '', summary: '', stageText: '', detail: '', retryAt: null };
  }
  const base = {
    phase: 'none',
    label: '',
    tone: 'info',
    text: '',
    summary: '',
    stageText: stageText(job),
    detail: (job.error && job.error.detail) || '',
    retryAt: job.retry_at || null,
  };

  if (job.status === 'completed') {
    const start = dayjs(job.started_at);
    const end = dayjs(job.completed_at);
    let text = '已完成';
    if (start.isValid() && end.isValid()) {
      const endText = start.isSame(end, 'day') ? end.format('HH:mm') : end.format('MM-DD HH:mm');
      text = `已完成 ${start.format('MM-DD HH:mm')} → ${endText}`;
    }
    return { ...base, phase: 'completed', label: PHASE_LABEL.completed, tone: PHASE_TONE.completed, text, summary: text };
  }

  if (job.status === 'failed') {
    const end = preAnnotateAt(job.completed_at);
    const error = job.error || {};
    const legacy = splitErrorStage(job.error_message);
    const summary = String(error.summary || legacy.text || job.error_message || '预标注失败').trim();
    const stageKey = error.stage || job.stage || legacy.stage;
    const name = STAGE_NAME[stageKey] || (legacy.stage && STAGE_NAME[legacy.stage]) || '';
    const reason = `${name ? `${name}：` : ''}${summary}`;
    const text = `失败${end ? `（${end}）` : ''}${reason ? ` · ${reason}` : ''}`;
    return { ...base, phase: 'failed', label: PHASE_LABEL.failed, tone: PHASE_TONE.failed, text, summary, detail: error.detail || base.detail };
  }

  if (job.status === 'pending') {
    const retry = job.retry_at ? dayjs(job.retry_at) : null;
    const seconds = retry && retry.isAfter(dayjs()) ? retry.diff(dayjs(), 'second') : 0;
    const retryAt = seconds > 0
      ? `，约 ${seconds <= 120 ? `${seconds} 秒后` : preAnnotateAt(job.retry_at)} 自动重试`
      : '';
    const text = `排队中（${job.dispatched ? '等待重试' : '未派发'}）${retryAt}`;
    return { ...base, phase: 'pending', label: PHASE_LABEL.pending, tone: PHASE_TONE.pending, text, summary: text };
  }

  if (job.status === 'processing') {
    if (!job.dispatched) {
      const text = '派发中（等待远端受理）';
      return { ...base, phase: 'dispatching', label: PHASE_LABEL.dispatching, tone: PHASE_TONE.dispatching, text, summary: text };
    }
    const start = preAnnotateAt(job.started_at);
    const phase = STAGE_TEXT[job.stage] ? job.stage : 'dispatched';
    const text = [`已派发${start ? ` ${start}` : ''}`, base.stageText].filter(Boolean).join(' · ');
    return { ...base, phase, label: PHASE_LABEL[phase] || PHASE_LABEL.dispatching, tone: PHASE_TONE[phase] || 'info', text, summary: text };
  }

  return { ...base, phase: 'pending', label: PHASE_LABEL.pending, tone: PHASE_TONE.pending, text: '排队中', summary: '排队中' };
}

/** 任务生命周期文案（兼容旧调用）。 */
export function preAnnotateStatusText(job) {
  return preAnnotateState(job).text;
}

/** 阶段进度短文案（兼容旧调用）：`推理中 37/80`。 */
export function preAnnotateStageText(job) {
  return stageText(job);
}

/**
 * 预标注完成回执文案：写回标注平台的张数 + 未覆盖/未发现目标/漏产出结果附注。
 * 没有回执（任务还没完成）给空串，调用处不渲染这行。
 */
export function preAnnotateReceiptText(receipt) {
  if (!receipt) return '';
  const parts = [`已写回 LS ${Number(receipt.injected) || 0} 张`];
  if (Number(receipt.existing) > 0) parts.push(`已有预测未覆盖 ${receipt.existing} 张`);
  if (Number(receipt.empty) > 0) parts.push(`未发现目标 ${receipt.empty} 张（建议人工复核）`);
  if (Number(receipt.unprocessed) > 0) parts.push(`漏产出结果 ${receipt.unprocessed} 张（可重试补齐）`);
  return `预标注回执：${parts.join(' · ')}`;
}
